import type { ValidationErrorObj } from '@/models/error';
import { getCurrentHyphenatedDate } from '@/utils/date';
import { getEnEmailRequiredAndFormatErrorMessage } from '@/utils/enValidator';

import type { WabunkaWaitlistCreateInputRequest } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_waitlist';

// useGlobalWabunkaReservationForm.ts の MAX_EMAIL_LENGTH と同じ上限を踏襲する
const MAX_EMAIL_LENGTH = 250;

export interface WaitlistRegistrationForm {
  email: string;
  preferredDateFrom: string;
  preferredDateTo: string;
}

export const createDefaultWaitlistRegistrationForm = (): WaitlistRegistrationForm => ({
  email: '',
  preferredDateFrom: '',
  preferredDateTo: '',
});

/**
 * 希望日程範囲の前後関係を検証する
 * 片方のみ・両方未指定は許容し、両方指定時のみ from <= to を要求する
 */
export const isValidDateRange = (from: string, to: string): boolean => {
  if (!from || !to) return true;
  return from <= to;
};

/**
 * 希望日程の終了日が通知対象になり得るかを検証する
 * 通知バッチの対象は未来日程のみのため、終了日が過去だと登録は成功するのに一度も通知されない
 * 体験日程は JST 基準なので、実行環境のローカル日ではなく JST の今日と比べる
 */
export const isNotifiableDateTo = (to: string): boolean => {
  if (!to) return true;
  return to >= getCurrentHyphenatedDate();
};

export const validateWaitlistRegistrationForm = (form: WaitlistRegistrationForm): ValidationErrorObj[] => {
  const errors: ValidationErrorObj[] = [];

  const emailMessage = getEnEmailRequiredAndFormatErrorMessage(form.email);
  if (emailMessage) {
    errors.push({ fieldName: 'email', message: emailMessage });
  } else if (form.email.length > MAX_EMAIL_LENGTH) {
    errors.push({ fieldName: 'email', message: `${MAX_EMAIL_LENGTH} characters or less` });
  }

  if (!isValidDateRange(form.preferredDateFrom, form.preferredDateTo)) {
    errors.push({ fieldName: 'preferredDateTo', message: 'The end date must be on or after the start date.' });
  } else if (!isNotifiableDateTo(form.preferredDateTo)) {
    errors.push({ fieldName: 'preferredDateTo', message: 'The end date must be today or later.' });
  }

  return errors;
};

export const buildWaitlistRegistrationRequest = (form: WaitlistRegistrationForm, experienceId: string): WabunkaWaitlistCreateInputRequest => ({
  experienceId,
  email: form.email,
  targetDateFrom: form.preferredDateFrom || undefined,
  targetDateTo: form.preferredDateTo || undefined,
});
